import { type Brand } from 'common/types/brand';

type IconFamily = 'light' | 'regular' | 'solid' | 'brands' | 'custom';
export type Icon = Brand<[IconFamily, string], 'Icon'>;

function makeIcon(family: IconFamily, name: string): Icon {
  return [family, name] as Icon;
}

export function maybeIcon(value: unknown): Icon | null {
  if (!Array.isArray(value)) {
    return null;
  }
  if (value.length !== 2) {
    return null;
  }
  const [iconFamily, iconName] = value;
  if (
    (iconFamily !== 'light' &&
      iconFamily !== 'regular' &&
      iconFamily !== 'solid' &&
      iconFamily !== 'custom' &&
      iconFamily !== 'brands') ||
    typeof iconName !== 'string'
  ) {
    return null;
  }

  return makeIcon(iconFamily, iconName);
}

export function assertIcon(value: unknown): Icon {
  const icon = maybeIcon(value);
  if (!icon) {
    const jsonValue = JSON.stringify(value);
    throw new Error(
      `Not an icon: ${jsonValue === undefined ? 'undefined' : jsonValue}`
    );
  }
  return icon;
}

export function iconFamilyClass(iconFamily: IconFamily): string {
  if (iconFamily === 'custom') {
    return 'fa-kit';
  }
  return `fa-${iconFamily}`;
}

// START_GENERATED_CODE
// Do not make manual edits here, they will be overwritten!
//
// If you want to add new icons, go add them to our custom Font Awesome kit:
// https://fontawesome.com/kits
//
// Then run the following the script:
//
//     just deps upgrade-font-awesome-v6
//

// Light icons
export const lightAngleRight = makeIcon('light', 'angle-right');
export const lightBars = makeIcon('light', 'bars');
export const lightBolt = makeIcon('light', 'bolt');
export const lightBriefcase = makeIcon('light', 'briefcase');
export const lightBurgerFries = makeIcon('light', 'burger-fries');
export const lightCakeCandles = makeIcon('light', 'cake-candles');
export const lightCalendarDays = makeIcon('light', 'calendar-days');
export const lightChalkboard = makeIcon('light', 'chalkboard');
export const lightCheck = makeIcon('light', 'check');
export const lightCircleXmark = makeIcon('light', 'circle-xmark');
export const lightClock = makeIcon('light', 'clock');
export const lightCoins = makeIcon('light', 'coins');
export const lightEnvelope = makeIcon('light', 'envelope');
export const lightFilterList = makeIcon('light', 'filter-list');
export const lightGlobe = makeIcon('light', 'globe');
export const lightHandshake = makeIcon('light', 'handshake');
export const lightHouse = makeIcon('light', 'house');
export const lightHouseBuilding = makeIcon('light', 'house-building');
export const lightLocationDot = makeIcon('light', 'location-dot');
export const lightMagnifyingGlass = makeIcon('light', 'magnifying-glass');
export const lightMicrophoneStand = makeIcon('light', 'microphone-stand');
export const lightMugHot = makeIcon('light', 'mug-hot');
export const lightPartyHorn = makeIcon('light', 'party-horn');
export const lightPhoneFlip = makeIcon('light', 'phone-flip');
export const lightPiggyBank = makeIcon('light', 'piggy-bank');
export const lightShapes = makeIcon('light', 'shapes');
export const lightSlash = makeIcon('light', 'slash');
export const lightSliders = makeIcon('light', 'sliders');
export const lightStar = makeIcon('light', 'star');
export const lightTag = makeIcon('light', 'tag');
export const lightTags = makeIcon('light', 'tags');
export const lightTrashCan = makeIcon('light', 'trash-can');
export const lightTurnUp = makeIcon('light', 'turn-up');
export const lightUser = makeIcon('light', 'user');
export const lightUserGroup = makeIcon('light', 'user-group');
export const lightXmark = makeIcon('light', 'xmark');

// Regular icons
export const regularAngleDown = makeIcon('regular', 'angle-down');
export const regularAngleRight = makeIcon('regular', 'angle-right');
export const regularAngleUp = makeIcon('regular', 'angle-up');
export const regularArrowRight = makeIcon('regular', 'arrow-right');
export const regularArrowUpRight = makeIcon('regular', 'arrow-up-right');
export const regularArrowUpRightFromSquare = makeIcon(
  'regular',
  'arrow-up-right-from-square'
);
export const regularArrowsRotate = makeIcon('regular', 'arrows-rotate');
export const regularBomb = makeIcon('regular', 'bomb');
export const regularBookmark = makeIcon('regular', 'bookmark');
export const regularBriefcase = makeIcon('regular', 'briefcase');
export const regularBuilding = makeIcon('regular', 'building');
export const regularBuildingColumns = makeIcon('regular', 'building-columns');
export const regularBuildings = makeIcon('regular', 'buildings');
export const regularCalendarCheck = makeIcon('regular', 'calendar-check');
export const regularCalendarDays = makeIcon('regular', 'calendar-days');
export const regularChartBar = makeIcon('regular', 'chart-bar');
export const regularChartLine = makeIcon('regular', 'chart-line');
export const regularCheck = makeIcon('regular', 'check');
export const regularCircle = makeIcon('regular', 'circle');
export const regularCircleCheck = makeIcon('regular', 'circle-check');
export const regularCircleDollar = makeIcon('regular', 'circle-dollar');
export const regularCircleXmark = makeIcon('regular', 'circle-xmark');
export const regularClock = makeIcon('regular', 'clock');
export const regularComment = makeIcon('regular', 'comment');
export const regularCommentDots = makeIcon('regular', 'comment-dots');
export const regularDollarSign = makeIcon('regular', 'dollar-sign');
export const regularDownload = makeIcon('regular', 'download');
export const regularEnvelope = makeIcon('regular', 'envelope');
export const regularEnvelopesBulk = makeIcon('regular', 'envelopes-bulk');
export const regularEye = makeIcon('regular', 'eye');
export const regularFile = makeIcon('regular', 'file');
export const regularFileAudio = makeIcon('regular', 'file-audio');
export const regularFileCSV = makeIcon('regular', 'file-csv');
export const regularFileExcel = makeIcon('regular', 'file-excel');
export const regularFileImage = makeIcon('regular', 'file-image');
export const regularFileLines = makeIcon('regular', 'file-lines');
export const regularFilePdf = makeIcon('regular', 'file-pdf');
export const regularFileWord = makeIcon('regular', 'file-word');
export const regularFileZipper = makeIcon('regular', 'file-zipper');
export const regularFiles = makeIcon('regular', 'files');
export const regularFilm = makeIcon('regular', 'film');
export const regularFloppyDisk = makeIcon('regular', 'floppy-disk');
export const regularGem = makeIcon('regular', 'gem');
export const regularGlobe = makeIcon('regular', 'globe');
export const regularHeart = makeIcon('regular', 'heart');
export const regularHouse = makeIcon('regular', 'house');
export const regularImage = makeIcon('regular', 'image');
export const regularImages = makeIcon('regular', 'images');
export const regularInbox = makeIcon('regular', 'inbox');
export const regularInboxOut = makeIcon('regular', 'inbox-out');
export const regularIndustry = makeIcon('regular', 'industry');
export const regularLayerGroup = makeIcon('regular', 'layer-group');
export const regularLink = makeIcon('regular', 'link');
export const regularList = makeIcon('regular', 'list');
export const regularLocationDot = makeIcon('regular', 'location-dot');
export const regularMagnifyingGlass = makeIcon('regular', 'magnifying-glass');
export const regularMinus = makeIcon('regular', 'minus');
export const regularMoneyBill1 = makeIcon('regular', 'money-bill-1');
export const regularObjectGroup = makeIcon('regular', 'object-group');
export const regularPaperPlane = makeIcon('regular', 'paper-plane');
export const regularPaperclip = makeIcon('regular', 'paperclip');
export const regularPenToSquare = makeIcon('regular', 'pen-to-square');
export const regularPlus = makeIcon('regular', 'plus');
export const regularQuestion = makeIcon('regular', 'question');
export const regularRectangleList = makeIcon('regular', 'rectangle-list');
export const regularRocket = makeIcon('regular', 'rocket');
export const regularSparkles = makeIcon('regular', 'sparkles');
export const regularSpinner = makeIcon('regular', 'spinner');
export const regularSquare = makeIcon('regular', 'square');
export const regularSquareCheck = makeIcon('regular', 'square-check');
export const regularStar = makeIcon('regular', 'star');
export const regularStarHalfStroke = makeIcon('regular', 'star-half-stroke');
export const regularTag = makeIcon('regular', 'tag');
export const regularTags = makeIcon('regular', 'tags');
export const regularTrashCan = makeIcon('regular', 'trash-can');
export const regularTriangleExclamation = makeIcon(
  'regular',
  'triangle-exclamation'
);
export const regularUser = makeIcon('regular', 'user');
export const regularUsers = makeIcon('regular', 'users');
export const regularUtensils = makeIcon('regular', 'utensils');

// Solid icons
export const solidAngleDown = makeIcon('solid', 'angle-down');
export const solidAngleLeft = makeIcon('solid', 'angle-left');
export const solidAngleRight = makeIcon('solid', 'angle-right');
export const solidAngleUp = makeIcon('solid', 'angle-up');
export const solidArrowDown = makeIcon('solid', 'arrow-down');
export const solidArrowsRotate = makeIcon('solid', 'arrows-rotate');
export const solidAt = makeIcon('solid', 'at');
export const solidBars = makeIcon('solid', 'bars');
export const solidBolt = makeIcon('solid', 'bolt');
export const solidBomb = makeIcon('solid', 'bomb');
export const solidBookmark = makeIcon('solid', 'bookmark');
export const solidBriefcase = makeIcon('solid', 'briefcase');
export const solidBuilding = makeIcon('solid', 'building');
export const solidBuildingColumns = makeIcon('solid', 'building-columns');
export const solidBullhorn = makeIcon('solid', 'bullhorn');
export const solidCalculator = makeIcon('solid', 'calculator');
export const solidCalendarDays = makeIcon('solid', 'calendar-days');
export const solidCaretDown = makeIcon('solid', 'caret-down');
export const solidCaretLeft = makeIcon('solid', 'caret-left');
export const solidCaretRight = makeIcon('solid', 'caret-right');
export const solidCaretUp = makeIcon('solid', 'caret-up');
export const solidChartBar = makeIcon('solid', 'chart-bar');
export const solidChartLine = makeIcon('solid', 'chart-line');
export const solidCheck = makeIcon('solid', 'check');
export const solidChevronDown = makeIcon('solid', 'chevron-down');
export const solidChevronLeft = makeIcon('solid', 'chevron-left');
export const solidChevronRight = makeIcon('solid', 'chevron-right');
export const solidChevronUp = makeIcon('solid', 'chevron-up');
export const solidCircleCheck = makeIcon('solid', 'circle-check');
export const solidCircleExclamation = makeIcon('solid', 'circle-exclamation');
export const solidCircleInfo = makeIcon('solid', 'circle-info');
export const solidCirclePlus = makeIcon('solid', 'circle-plus');
export const solidCircleQuestion = makeIcon('solid', 'circle-question');
export const solidCircleXmark = makeIcon('solid', 'circle-xmark');
export const solidComment = makeIcon('solid', 'comment');
export const solidCommentDots = makeIcon('solid', 'comment-dots');
export const solidComments = makeIcon('solid', 'comments');
export const solidDollarSign = makeIcon('solid', 'dollar-sign');
export const solidDownload = makeIcon('solid', 'download');
export const solidEnvelope = makeIcon('solid', 'envelope');
export const solidEye = makeIcon('solid', 'eye');
export const solidEyeSlash = makeIcon('solid', 'eye-slash');
export const solidFilm = makeIcon('solid', 'film');
export const solidGift = makeIcon('solid', 'gift');
export const solidGlobe = makeIcon('solid', 'globe');
export const solidHatChef = makeIcon('solid', 'hat-chef');
export const solidHeart = makeIcon('solid', 'heart');
export const solidHouse = makeIcon('solid', 'house');
export const solidIndustry = makeIcon('solid', 'industry');
export const solidLanguage = makeIcon('solid', 'language');
export const solidList = makeIcon('solid', 'list');
export const solidListUl = makeIcon('solid', 'list-ul');
export const solidLocationDot = makeIcon('solid', 'location-dot');
export const solidLocationPin = makeIcon('solid', 'location-pin');
export const solidLock = makeIcon('solid', 'lock');
export const solidMagnifyingGlass = makeIcon('solid', 'magnifying-glass');
export const solidMoneyBill1 = makeIcon('solid', 'money-bill-1');
export const solidObjectGroup = makeIcon('solid', 'object-group');
export const solidPaperPlane = makeIcon('solid', 'paper-plane');
export const solidPaperclip = makeIcon('solid', 'paperclip');
export const solidPenToSquare = makeIcon('solid', 'pen-to-square');
export const solidPerson = makeIcon('solid', 'person');
export const solidPhone = makeIcon('solid', 'phone');
export const solidPlus = makeIcon('solid', 'plus');
export const solidQuestion = makeIcon('solid', 'question');
export const solidRocket = makeIcon('solid', 'rocket');
export const solidSliders = makeIcon('solid', 'sliders');
export const solidSort = makeIcon('solid', 'sort');
export const solidSortDown = makeIcon('solid', 'sort-down');
export const solidSortUp = makeIcon('solid', 'sort-up');
export const solidSpinner = makeIcon('solid', 'spinner');
export const solidSquareCheck = makeIcon('solid', 'square-check');
export const solidSquarePen = makeIcon('solid', 'square-pen');
export const solidStar = makeIcon('solid', 'star');
export const solidStreetView = makeIcon('solid', 'street-view');
export const solidTag = makeIcon('solid', 'tag');
export const solidTags = makeIcon('solid', 'tags');
export const solidTrashCan = makeIcon('solid', 'trash-can');
export const solidTriangleExclamation = makeIcon(
  'solid',
  'triangle-exclamation'
);
export const solidUpload = makeIcon('solid', 'upload');
export const solidUser = makeIcon('solid', 'user');
export const solidUsers = makeIcon('solid', 'users');
export const solidUtensils = makeIcon('solid', 'utensils');
export const solidWrench = makeIcon('solid', 'wrench');
export const solidXmark = makeIcon('solid', 'xmark');

// Brands icons
export const brandsCcStripe = makeIcon('brands', 'cc-stripe');
export const brandsSquareFacebook = makeIcon('brands', 'square-facebook');
export const brandsSquareTwitter = makeIcon('brands', 'square-twitter');
export const brandsYoutube = makeIcon('brands', 'youtube');

// Custom icons
export const customBottleAndCan = makeIcon('custom', 'bottle-and-can');
export const customForkAndGlass = makeIcon('custom', 'fork-and-glass');
export const customSitting = makeIcon('custom', 'sitting');
export const customVenuePackage = makeIcon('custom', 'venue-package');

// END_GENERATED_CODE

export const blankIcon = makeIcon('solid', '');

// Old aliases. TODO: Remove these and use proper names instead
export const lightBirthdayCake = lightCakeCandles;
export const lightCalendarAlt = lightCalendarDays;
export const lightHome = lightHouse;
export const lightPhone = lightPhoneFlip;
export const lightTimesCircle = lightCircleXmark;
export const lightTrash = lightTrashCan;
export const lightUserFriends = lightUserGroup;
export const regularEdit = regularPenToSquare;
export const regularExclamationTriangle = regularTriangleExclamation;
export const regularExternalLink = regularArrowUpRightFromSquare;
export const regularSearch = regularMagnifyingGlass;
export const solidCalendarAlt = solidCalendarDays;
export const solidCheckSquare = solidSquareCheck;
export const solidExclamationTriangle = solidTriangleExclamation;
export const solidMapMarker = solidLocationPin;
export const solidSearch = solidMagnifyingGlass;
